import React from 'react';
import { AlertCircle } from 'lucide-react';

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  name: string;
  label: string;
  options: RadioOption[];
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
  helperText?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * RadioGroup Component
 * 
 * A reusable radio button group with enhanced UX:
 * - Visual selection states
 * - Support for descriptions
 * - Keyboard navigation
 * - Error states
 * - Helper text
 * 
 * Use this instead of dropdowns when you have ≤5 options
 */
export const RadioGroup: React.FC<RadioGroupProps> = ({
  name,
  label,
  options,
  value,
  onChange,
  required = false,
  error,
  helperText,
  disabled = false,
  className = ''
}) => {
  // If more than 5 options, log warning (should use select instead)
  React.useEffect(() => {
    if (options.length > 5) {
      console.warn(
        `RadioGroup for "${name}" has ${options.length} options. ` +
        'Consider using a select dropdown for better UX when there are more than 5 options.'
      );
    }
  }, [options.length, name]);

  return (
    <fieldset className={`space-y-3 ${className}`} disabled={disabled}>
      {/* Legend (Label) */}
      <legend className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-error-500 ml-1">*</span>}
      </legend>

      {/* Helper Text */}
      {helperText && !error && (
        <p className="text-xs text-gray-600 -mt-1">
          {helperText}
        </p>
      )}

      {/* Radio Options - Horizontal Layout for Space Efficiency */}
      <div className="flex flex-wrap gap-3" role="radiogroup" aria-label={label}>
        {options.map((option) => {
          const isSelected = value === option.value;
          const isDisabled = disabled || option.disabled;

          return (
            <label
              key={option.value}
              className={`
                flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-all cursor-pointer
                ${isSelected
                  ? 'border-info-500 bg-info-50 text-info-700 font-medium'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}
                ${isDisabled
                  ? 'opacity-50 cursor-not-allowed'
                  : ''}
                ${error
                  ? 'border-error-300'
                  : ''}
              `}
            >
              {/* Radio Input */}
              <input
                type="radio"
                name={name}
                value={option.value}
                checked={isSelected}
                onChange={(e) => onChange(e.target.value)}
                disabled={isDisabled}
                className="w-4 h-4 text-info-600 focus:ring-2 focus:ring-info-500 focus:ring-offset-0"
                aria-checked={isSelected}
                aria-describedby={option.description ? `${name}-${option.value}-desc` : undefined}
              />

              {/* Label */}
              <span className="text-sm whitespace-nowrap">
                {option.label}
              </span>

              {/* Selected Indicator */}
              {isSelected && (
                <svg className="w-4 h-4 text-info-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </label>
          );
        })}
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-1.5 mt-2" role="alert">
          <AlertCircle className="w-4 h-4 text-error-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-error-600">
            {error}
          </p>
        </div>
      )}
    </fieldset>
  );
};

export default RadioGroup;
